import {useMemo} from 'react';

import {useI18n} from '@udemy/i18n';
import {useCoursesForCartUpsellByIds} from '@udemy/react-certification-preparation';
import {ShoppingItem as UdemyShoppingItem} from '@udemy/shopping';

import {useBundleCoursesForVoucher} from 'src/hooks/use-bundle-courses-for-voucher';
import {useCoursePrice} from 'src/hooks/use-course-price';

import {
    buildShoppingItemFromGqlCourse,
    isEffectivelyFree,
} from '../certification-upsell-shopping-item-builders';

export interface UseCertificationVoucherUpsellParams {
    offerId: string;
    cartCourseIds?: Set<number>;
}

export interface UseCertificationVoucherUpsellResult {
    courseItems: UdemyShoppingItem[];
    hasContent: boolean;
    isLoading: boolean;
}

export function useCertificationVoucherUpsell({
    offerId,
    cartCourseIds,
}: UseCertificationVoucherUpsellParams): UseCertificationVoucherUpsellResult {
    const {gettext} = useI18n();

    const {courseIdsToUpsell, isLoading: bundleLoading} = useBundleCoursesForVoucher(
        offerId,
        cartCourseIds,
    );

    const {courses, isLoading: coursesLoading} = useCoursesForCartUpsellByIds(courseIdsToUpsell);

    // useCoursePrice must be called unconditionally (hooks rules) — bundles have at most 2 courses.
    const {price: coursePrice0, isLoading: coursePriceLoading0} = useCoursePrice(
        courseIdsToUpsell[0] ?? null,
    );
    const {price: coursePrice1, isLoading: coursePriceLoading1} = useCoursePrice(
        courseIdsToUpsell[1] ?? null,
    );

    const courseItems = useMemo(() => {
        if (coursesLoading) return [];
        const prices = [coursePrice0, coursePrice1];
        const pricesLoading = [coursePriceLoading0, coursePriceLoading1];
        return courses
            .map((course, i) => {
                if (!course || course.isEnrolled) return null;
                const candidate = buildShoppingItemFromGqlCourse(course, prices[i], gettext);
                if (!candidate) return null;
                if (isEffectivelyFree(candidate, pricesLoading[i])) return null;
                return candidate;
            })
            .filter((course) => course != null);
    }, [
        courses,
        coursesLoading,
        coursePrice0,
        coursePrice1,
        coursePriceLoading0,
        coursePriceLoading1,
        gettext,
    ]);

    const isLoading = bundleLoading || coursesLoading || coursePriceLoading0 || coursePriceLoading1;
    const hasContent = !isLoading && courseItems.length > 0;

    return {courseItems, hasContent, isLoading};
}
